// The "install AcyMart" offer on mobile web.
//
// Modelled on the install banner news sites use (Evening Standard, Guardian):
// a card at the top of the page carrying the app icon, its name, the domain it
// installs from, and a single Install action. Two things make that shape work,
// and both are deliberate here:
//
//   · It says what it IS. "Install AcyMart / acymart.com" tells the visitor
//     precisely what is about to be added to their home screen and where it
//     comes from. The previous copy ("Get the AcyMart app / Faster browsing,
//     works offline") was marketing for an unnamed thing.
//   · Install INSTALLS. It opens the browser's own install dialog on the spot
//     rather than routing to a page that then explains installing. Every extra
//     screen between the offer and the outcome loses people.
//
// The exception is iOS, where WebKit fires no beforeinstallprompt and there is
// no API to open anything -- Safari's Share > Add to Home Screen is the only
// route. There the action has to lead to the instructions on /app instead, so
// it renders as a link rather than a button. Same for an Android visitor whose
// prompt event has not fired (or has been spent), where /app still offers the
// APK.
//
// It deliberately does NOT appear:
//   · inside the packaged app (there is nothing to install)
//   · once the PWA is installed (display-mode: standalone)
//   · on desktop, where the phone app is not the point
//   · when there is no APK published and no PWA prompt available, i.e. when
//     tapping it could not lead anywhere
import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import usePwaInstall from './usePwaInstall';
import { LogoIcon } from './Logo';
import { IconX } from './Icons';

const DISMISS_KEY = 'acymart_install_banner_dismissed_at';
const DISMISS_DAYS = 30;

// localStorage throws outright in some privacy modes rather than returning
// null, and a storage failure must never take the storefront down with it --
// so both sides are wrapped and a failure just means "not dismissed".
function wasDismissed() {
  try {
    const at = Number(window.localStorage.getItem(DISMISS_KEY));
    if (!at) return false;
    return (Date.now() - at) < DISMISS_DAYS * 24 * 60 * 60 * 1000;
  } catch {
    return false;
  }
}

// What the visitor is installing FROM, read from the address bar rather than
// hardcoded. On acymart.com it says acymart.com; on a staging host or a phone
// reaching a dev machine over the LAN it says that instead, which is the
// truthful answer -- a banner claiming acymart.com while served from somewhere
// else is exactly the confusion this line exists to prevent.
function installOrigin() {
  try {
    return window.location.hostname.replace(/^www\./, '');
  } catch {
    return 'acymart.com';
  }
}

export default function InstallAppBanner() {
  const { platform, installed, inNativeApp, canPromptInstall, promptInstall } = usePwaInstall();
  const [dismissed, setDismissed] = useState(true);
  const [busy, setBusy] = useState(false);

  // Read from localStorage in an effect rather than in useState's initialiser
  // so the server-rendered shell and the first client render agree. Reading it
  // during render makes the banner flash in and out on a hydrating load.
  useEffect(() => { setDismissed(wasDismissed()); }, []);

  const remember = useCallback(() => {
    setDismissed(true);
    try { window.localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* private mode */ }
  }, []);

  const handleInstall = useCallback(async () => {
    if (busy) return;
    setBusy(true);
    // Resolves false when the visitor dismissed the browser's dialog. Either
    // way the banner goes: Chrome will not honour the same deferred event
    // twice, so leaving it on screen would give them a button that silently
    // does nothing. On acceptance `appinstalled` hides it regardless.
    await promptInstall();
    setBusy(false);
    remember();
  }, [busy, promptInstall, remember]);

  const isPhone = platform === 'android' || platform === 'ios';
  // On iOS there is no install event to wait for -- /app's manual Add to Home
  // Screen instructions are the destination, and they are always worth showing.
  // On Android the banner leads somewhere useful whether the APK or the PWA
  // prompt is what is available.
  const worthShowing = platform === 'ios' || canPromptInstall || platform === 'android';

  if (inNativeApp || installed || dismissed || !isPhone || !worthShowing) return null;

  return (
    <div className="pwa-install" role="complementary" aria-label="Install AcyMart">
      <span className="pwa-install-icon">
        <LogoIcon size={34} />
      </span>

      <div className="pwa-install-text">
        <strong className="pwa-install-title">Install AcyMart</strong>
        <span className="pwa-install-origin">{installOrigin()}</span>
      </div>

      {/* A button when it can genuinely install, a link when the only honest
          next step is instructions. Same slot, same styling -- the difference
          is which element can actually deliver on the word "Install". */}
      {canPromptInstall ? (
        <button type="button" className="pwa-install-cta" onClick={handleInstall} disabled={busy}>
          {busy ? 'Opening…' : 'Install'}
        </button>
      ) : (
        <Link className="pwa-install-cta" to="/app" onClick={remember}>
          Install
        </Link>
      )}

      {/* Not in the pattern this copies, which offers no way out. Kept because
          an offer you cannot decline is a nag: this one goes away for 30 days. */}
      <button
        type="button"
        className="pwa-install-close"
        onClick={remember}
        aria-label="Not now — hide the install offer"
      >
        <IconX size={15} />
      </button>
    </div>
  );
}
